/**
 * Ring op. A NEW ring/shell from two SIGNED cm offsets: `innerCm` is
 * the LOWER (inner) signed offset, `outerCm` the UPPER (outer), both measured
 * from the source surface (negative = inward, positive = outward, 0 = the
 * surface). Runs the shared staged pipeline and reuses the single cm→mm
 * choke-point, branded `Mm`, and clinical bound; a ring is the boolean
 * difference of the two offsets.
 */
import {
    cmToMmChokepoint,
    resolveSourceForNewStructure,
    previewSingleCall,
    commitComputed,
    MAX_ABS_MARGIN_CM,
    type Brand,
    type Mm,
    type ComputedGeometry,
    type OpDeps,
    type ValidateResult,
    type CommitResult,
} from '../core';
import type { CreateRingInput } from '../../contract';

export type RingInput = CreateRingInput;

export type RingDeps = OpDeps<ComputedGeometry> & {
    readonly geometry: {
        /** Lower/inner first, upper/outer second — `outerMm` > `innerMm`. */
        applyRing(
            sourceRoiNumber: number,
            innerMm: Mm,
            outerMm: Mm
        ): Promise<ComputedGeometry>;
    };
};

/* ── Stage 1: resolve (target + units + invariants) ── */

interface RingValidatedData {
    readonly sourceRoiNumber: number;
    readonly sourceVolumeCm3: number;
    /** Lower (inner) signed offset, minted at the cm→mm choke-point. */
    readonly innerMm: Mm;
    /** Upper (outer) signed offset; strictly greater than `innerMm`. */
    readonly outerMm: Mm;
    readonly newRoi: string;
}
/** Only `resolveRing` mints this (the brand is unforgeable without an
 *  explicit cast) — preview cannot be skipped. */
export type RingValidated = Brand<RingValidatedData, 'RingValidated'>;

export function resolveRing(
    input: RingInput,
    deps: RingDeps
): ValidateResult<RingValidated> {
    // Fail-fast on the pure, dependency-free numeric invariants first.
    const { innerCm, outerCm } = input;
    if (!Number.isFinite(innerCm) || !Number.isFinite(outerCm)) {
        return {
            kind: 'invalid',
            reason: 'inner and outer offsets must be finite centimetres',
        };
    }
    // The locked ring invariant: a zero-width or inverted ring is not a
    // structure. `SDFOperations.applyRing` throws on upper ≤ lower, so this
    // is rejected before any geometry work.
    if (outerCm <= innerCm) {
        return {
            kind: 'invalid',
            reason:
                `outer offset ${outerCm} cm must be strictly greater than ` +
                `inner offset ${innerCm} cm (a zero-width or inverted ring ` +
                'is not valid)',
        };
    }
    if (
        Math.abs(innerCm) > MAX_ABS_MARGIN_CM ||
        Math.abs(outerCm) > MAX_ABS_MARGIN_CM
    ) {
        return {
            kind: 'invalid',
            reason:
                `a ring offset is outside the safe ±${MAX_ABS_MARGIN_CM} cm ` +
                'range (possible cm/mm mix-up)',
        };
    }

    const resolved = resolveSourceForNewStructure(
        input.roi,
        input.newRoi,
        deps.structures,
        'ring',
        'build a ring from'
    );
    if (resolved.kind !== 'source') {
        return resolved;
    }

    const validated = {
        sourceRoiNumber: resolved.source.roiNumber,
        sourceVolumeCm3: resolved.source.volumeCm3,
        innerMm: cmToMmChokepoint(input.innerCm),
        outerMm: cmToMmChokepoint(input.outerCm),
        newRoi: input.newRoi,
    } as RingValidated;
    return { kind: 'validated', validated };
}

/* ── Stage 2: preview (compute geometry ONCE) ── */

interface RingPreviewedData {
    readonly computed: ComputedGeometry;
    readonly newRoi: string;
    readonly volumeDeltaCm3: number;
}
/** Only `previewRing` mints this; `commitRing` accepts only this, so the
 *  applied geometry IS the previewed geometry (no recompute path). */
export type RingPreviewed = Brand<RingPreviewedData, 'RingPreviewed'>;

export type RingPreviewResult =
    | { readonly kind: 'previewed'; readonly previewed: RingPreviewed }
    | { readonly kind: 'failed'; readonly reason: string };

export async function previewRing(
    validated: RingValidated,
    deps: RingDeps
): Promise<RingPreviewResult> {
    const r = await previewSingleCall(
        validated,
        (v) => deps.geometry.applyRing(v.sourceRoiNumber, v.innerMm, v.outerMm),
        deps.geometry.releaseComputed,
        'the resulting ring would be empty'
    );
    return r.kind === 'failed'
        ? r
        : { kind: 'previewed', previewed: r.previewed as RingPreviewed };
}

/* ── Stage 3: commit (write the ALREADY-computed geometry to a NEW ROI) ── */

export async function commitRing(
    previewed: RingPreviewed,
    deps: RingDeps
): Promise<CommitResult> {
    return commitComputed(previewed, deps.commit.createNewStructure);
}
